import { useState, useEffect, useRef } from "react";
import SEO from "../components/ui/SEO";
import "./PayPage.css";

const RAZORPAY_KEY_ID = import.meta.env.VITE_RAZORPAY_KEY_ID;

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      resolve(window.Razorpay);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
    document.head.appendChild(script);
  });
}

export default function PayPage() {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error | cancelled
  const [message, setMessage] = useState("");
  const [loadingSdk, setLoadingSdk] = useState(true);
  const [sdkError, setSdkError] = useState(null);
  const formRef = useRef(null);
  const paymentHandled = useRef(false);

  useEffect(() => {
    loadRazorpayScript()
      .then(() => setLoadingSdk(false))
      .catch((err) => {
        setSdkError(err.message);
        setLoadingSdk(false);
      });
  }, []);

  const amountInPaise = Math.round(parseFloat(amount) * 100);
  const isValid = amountInPaise >= 100;

  const handlePay = async (e) => {
    e.preventDefault();
    if (!isValid || status === "loading") return;

    setStatus("loading");
    setMessage("");
    paymentHandled.current = false;

    try {
      const orderRes = await fetch("/api/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: amountInPaise,
          currency: "INR",
          receipt: description.trim() || undefined,
        }),
      });

      if (!orderRes.ok) {
        const err = await orderRes.json();
        throw new Error(err.error || "Failed to create order");
      }

      const { order_id, amount: orderAmount, currency } = await orderRes.json();

      const rzp = new window.Razorpay({
        key: RAZORPAY_KEY_ID,
        amount: orderAmount,
        currency,
        description: description.trim(),
        order_id,
        handler: async (response) => {
          paymentHandled.current = true;
          try {
            const verifyRes = await fetch("/api/verify-payment", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(response),
            });

            const result = await verifyRes.json();

            if (result.verified) {
              setStatus("success");
              setMessage(`Payment successful! ID: ${response.razorpay_payment_id}`);
              setAmount("");
              setDescription("");
            } else {
              setStatus("error");
              setMessage("Payment verification failed. Please contact support.");
            }
          } catch {
            setStatus("error");
            setMessage("Could not verify payment. Please contact support.");
          }
        },
        prefill: {
          name: "",
          email: "",
          contact: "",
        },
        theme: {
          color: "#4f46e5",
        },
        modal: {
          ondismiss: () => {
            setStatus("cancelled");
            setMessage("Payment cancelled.");
          },
        },
      });

      rzp.on("payment.failed", (response) => {
        if (paymentHandled.current) return;
        setStatus("error");
        setMessage(response.error?.description || "Payment failed. Please try again.");
      });

      rzp.open();
    } catch (err) {
      setStatus("error");
      setMessage(err.message || "Something went wrong.");
    }
  };

  return (
    <main className="pay-page" aria-label="Main content">
      <SEO
        title="Support"
        description="Make a payment or support Rajat Shenoi's work."
        url="/pay"
      />
      <div className="container pay-page__inner">
        <h1 className="section-title">
          <span className="section-title__index">01</span>
          Make a Payment
        </h1>

        {sdkError && (
          <div className="pay-card pay-card--error">
            <p>Failed to load payment SDK: {sdkError}</p>
          </div>
        )}

        {status === "success" ? (
          <div className="pay-card pay-card--success">
            <div className="pay-success__icon">&#10003;</div>
            <p className="pay-success__text">{message}</p>
            <button
              className="btn btn-outline"
              onClick={() => { setStatus("idle"); setMessage(""); }}
            >
              Make another payment
            </button>
          </div>
        ) : (
          <form
            ref={formRef}
            className="pay-card"
            onSubmit={handlePay}
          >
            <label className="pay-field">
              <span className="pay-field__label">Amount (INR)</span>
              <div className="pay-field__input-wrap">
                <span className="pay-field__prefix">&#8377;</span>
                <input
                  className="pay-field__input"
                  type="number"
                  min="1"
                  step="any"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={status === "loading"}
                  autoFocus
                />
              </div>
              {amountInPaise > 0 && amountInPaise < 100 && (
                <span className="pay-field__hint pay-field__hint--warn">
                  Minimum amount is &#8377;1.00
                </span>
              )}
            </label>

            <label className="pay-field">
              <span className="pay-field__label">Description</span>
              <input
                className="pay-field__input"
                type="text"
                placeholder="What is this payment for?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={status === "loading"}
              />
            </label>

            {message && status === "error" && (
              <div className="pay-card__error">{message}</div>
            )}
            {status === "cancelled" && (
              <div className="pay-card__info">{message}</div>
            )}

            <button
              className="btn btn-primary pay-btn"
              type="submit"
              disabled={!isValid || loadingSdk || status === "loading"}
            >
              {status === "loading" ? (
                <span className="pay-btn__spinner" />
              ) : loadingSdk ? (
                "Loading..."
              ) : (
                `Pay ₹${amount || "0"}`
              )}
            </button>

            <p className="pay-footer">
              Payments processed securely via Razorpay.
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
